/* ============================================================================
   Slide 10 — "Chandra Bhedana" — SHARED CONTENT (single source of truth)
   Read by the desktop layout (10_chandraBhedana.tsx), the practice body
   (10_chandraBhedana_body.tsx) and the mobile layout (HTLDSlide10Mobile).
   Edit copy ONLY here — English and German side by side; the German object
   must have exactly the same shape as the English one.
   ========================================================================== */
import { ByLang, useL } from "i18n/lang";

export const accent10 = "#7b8ef0";

export interface Seg { t: string; b?: boolean; em?: boolean; }

const en = {
  meta: {
    breadcrumbPre: "Breath Practice",
    breadcrumbAccent: "The moon breath · gateway to sleep",
    sanskrit: "चन्द्र भेदन प्राणायाम",
    headerTitlePre: "Chandra ",
    headerTitleAccent: "Bhedana",
    titlePre: "Left Nostril ",
    titleAccentWord: "Breathing",
    titlePost: "",
    subtitle: "Chandra Bhedana · The moon breath — gateway to sleep",
    whyLabel: "Why the left nostril?",
    howLabel: "How to practise · 4 · 2 · 6",
    /** desktop shows this upper-cased */
    blockedLabel: "💡 If the left nostril is blocked",
    thresholdLabel: "The threshold",
    /** one-line summary under the title of the portrait practice guide */
    guideSummary: "Left nostril · 4 · 2 · 6 — 9 rounds",
  },
  steps: [
    { step: "Close the right nostril", desc: "Use the right thumb. Keep it gently closed throughout." },
    { step: "Inhale through LEFT — 4", desc: "Slow, deep belly breath. Feel the abdomen expand." },
    { step: "Hold gently — 2",         desc: "Brief, easy pause. No tension or strain." },
    { step: "Exhale through LEFT — 6", desc: "Long, slow release. The extended exhale deepens rest." },
    { step: "Repeat for 9 rounds",     desc: "Until the breath is effortless and awareness softens." },
  ] as { step: string; desc: string }[],
  why: [
    { t: "The left nostril connects to the " },
    { t: "Ida nadi", b: true },
    { t: " — the lunar, cooling energy channel associated with the parasympathetic nervous system and rest. Breathing exclusively through the left nostril lowers cortisol, slows the heart rate, and guides the brain toward the " },
    { t: "Theta wave state", b: true },
    { t: " that precedes REM sleep." },
  ] as Seg[],
  blocked: [
    { t: "Place a rolled towel under the " },
    { t: "right armpit", b: true },
    { t: ". This reflex action opens the left nostril naturally within a few minutes." },
  ] as Seg[],
  threshold: [
    { t: "The goal is not to fall unconscious — it is to arrive at the edge of sleep with " },
    { t: "awareness still present.", b: true },
    { t: " That threshold — the hypnagogic state — is where Dreamfulness begins." },
  ] as Seg[],
  /** text drawn on the chandra-guide canvas */
  canvas: {
    inhale: "INHALE",
    hold: "HOLD",
    exhale: "EXHALE",
    /** phase-bar segment labels (counts are fixed: 4 · 2 · 6) */
    barInhale: "INHALE · 4",
    barHold: "HOLD · 2",
    barExhale: "EXHALE · 6",
    left: "LEFT",
    open: "OPEN",
    right: "RIGHT",
    closedThumb: "CLOSED · THUMB",
    caption: "chandra · the moon breath",
    pressToBegin: "press ▶ to begin",
    round: (n: number, total: number) => `round ${n} · of ${total}`,
  },
};

export type Slide10Content = typeof en;

const de: Slide10Content = {
  meta: {
    breadcrumbPre: "Atempraxis",
    breadcrumbAccent: "Der Mondatem · Tor zum Schlaf",
    sanskrit: "चन्द्र भेदन प्राणायाम",
    headerTitlePre: "Chandra ",
    headerTitleAccent: "Bhedana",
    titlePre: "Atmung durch das linke ",
    titleAccentWord: "Nasenloch",
    titlePost: "",
    subtitle: "Chandra Bhedana · Der Mondatem – Tor zum Schlaf",
    whyLabel: "Warum das linke Nasenloch?",
    howLabel: "So übst du · 4 · 2 · 6",
    blockedLabel: "💡 Wenn das linke Nasenloch verstopft ist",
    thresholdLabel: "Die Schwelle",
    guideSummary: "Linkes Nasenloch · 4 · 2 · 6 – 9 Runden",
  },
  steps: [
    { step: "Schließe das rechte Nasenloch", desc: "Mit dem rechten Daumen – die ganze Zeit sanft geschlossen halten." },
    { step: "Links einatmen – 4",            desc: "Langsamer, tiefer Bauchatem. Spüre, wie sich der Bauch weitet." },
    { step: "Sanft halten – 2",              desc: "Kurze, leichte Pause. Ohne Spannung oder Anstrengung." },
    { step: "Links ausatmen – 6",            desc: "Lang und langsam loslassen. Die längere Ausatmung vertieft die Ruhe." },
    { step: "9 Runden wiederholen",          desc: "Bis der Atem mühelos ist und das Bewusstsein weich wird." },
  ],
  why: [
    { t: "Das linke Nasenloch ist mit der " },
    { t: "Ida-Nadi", b: true },
    { t: " verbunden – dem lunaren, kühlenden Energiekanal, der dem parasympathischen Nervensystem und der Ruhe zugeordnet ist. Ausschließlich links zu atmen senkt Cortisol, verlangsamt den Herzschlag und führt das Gehirn in den " },
    { t: "Theta-Zustand", b: true },
    { t: ", der dem REM-Schlaf vorausgeht." },
  ],
  blocked: [
    { t: "Lege ein zusammengerolltes Handtuch unter die " },
    { t: "rechte Achsel", b: true },
    { t: ". Dieser Reflex öffnet das linke Nasenloch innerhalb weniger Minuten ganz natürlich." },
  ],
  threshold: [
    { t: "Ziel ist nicht, bewusstlos einzuschlafen – sondern an den Rand des Schlafs zu gelangen, " },
    { t: "während das Bewusstsein wach bleibt.", b: true },
    { t: " An dieser Schwelle – dem hypnagogen Zustand – beginnt Dreamfulness." },
  ],
  canvas: {
    inhale: "EINATMEN",
    hold: "HALTEN",
    exhale: "AUSATMEN",
    barInhale: "EINATMEN · 4",
    barHold: "HALTEN 2",
    barExhale: "AUSATMEN · 6",
    left: "LINKS",
    open: "OFFEN",
    right: "RECHTS",
    closedThumb: "GESCHLOSSEN · DAUMEN",
    caption: "Chandra · der Mondatem",
    pressToBegin: "drücke ▶ zum Starten",
    round: (n: number, total: number) => `Runde ${n} · von ${total}`,
  },
};

export const slide10Content: ByLang<Slide10Content> = { en, de };
export const useSlide10 = () => useL(slide10Content);
